import React from "react";
import { Link } from "react-router-dom";
import { Megaphone, ArrowRight } from "lucide-react";
import { useI18n } from "@/i18n";
import { useImpression, trackAd } from "@/lib/ads";

const Label = ({ text }) => (
  <span data-testid="sponsored-label" className="absolute top-0 start-0 z-10 bg-amber-400 text-amber-950 font-mono text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-ee-lg">{text}</span>
);

export function SponsoredCard({ card }) {
  const { t, lang } = useI18n();
  const ref = useImpression(card ? card.id : "sponsored_placeholder");
  if (!card) return <Placeholder innerRef={ref} />;
  const title = (lang === "ar" && card.title_ar) || card.title_en;
  const desc = (lang === "ar" && card.desc_ar) || card.desc_en;
  const click = () => trackAd(card.id, "click");

  return (
    <article ref={ref} data-testid="sponsored-card" className="group fade-up relative bg-white rounded-xl border-2 border-amber-300 overflow-hidden flex flex-col">
      <Label text={`${t("sponsored.label")} · ${card.sponsor}`} />
      <a href={card.target_url} target="_blank" rel="sponsored noopener noreferrer" onClick={click} className="block aspect-[16/9] bg-gradient-to-br from-amber-100 to-amber-50 overflow-hidden">
        {card.image_url ? <img src={card.image_url} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-300" /> : <span className="w-full h-full flex items-center justify-center"><Megaphone className="text-amber-400" size={46} strokeWidth={1.5} /></span>}
      </a>
      <div className="p-5 flex flex-col flex-1">
        <h3 data-testid="sponsored-title" className="font-display font-bold text-[17px] leading-snug tracking-tight text-slate-900">{title}</h3>
        {desc && <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed line-clamp-3">{desc}</p>}
        <a data-testid="sponsored-cta" href={card.target_url} target="_blank" rel="sponsored noopener noreferrer" onClick={click} className="mt-3 inline-flex items-center gap-1 text-amber-700 font-semibold text-sm hover:gap-1.5 transition-all">
          {t("sponsored.learnMore")} <ArrowRight size={14} className="rtl:rotate-180" />
        </a>
        <p className="mt-auto pt-4 text-[11px] text-muted-foreground leading-relaxed">{t("sponsored.disclaimer")}</p>
      </div>
    </article>
  );
}

function Placeholder({ innerRef }) {
  const { t } = useI18n();
  return (
    <article ref={innerRef} data-testid="sponsored-card" className="group fade-up relative bg-white rounded-xl border-2 border-dashed border-amber-300 overflow-hidden flex flex-col">
      <Label text={t("sponsored.label")} />
      <div className="aspect-[16/9] bg-gradient-to-br from-amber-100 to-amber-50 flex items-center justify-center"><Megaphone className="text-amber-400" size={46} strokeWidth={1.5} /></div>
      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-display font-bold text-[17px] leading-snug tracking-tight text-slate-900">{t("sponsored.title")}</h3>
        <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed line-clamp-3">{t("sponsored.desc")}</p>
        <Link data-testid="sponsored-cta" to="/advertise" onClick={() => trackAd("sponsored_placeholder", "click")} className="mt-3 inline-flex items-center gap-1 text-amber-700 font-semibold text-sm hover:gap-1.5 transition-all">
          {t("sponsored.cta")} <ArrowRight size={14} className="rtl:rotate-180" />
        </Link>
        <p className="mt-auto pt-4 text-[11px] text-muted-foreground leading-relaxed">{t("sponsored.disclaimer")}</p>
      </div>
    </article>
  );
}
